import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { AlertTriangle, TrendingDown } from "lucide-react";

/**
 * IndexAlertsBanner — shows a warm, non-alarming heads-up when any
 * Health Index has recently dropped past the alert threshold.
 * Renders nothing when there are no alerts, so the UI stays clean.
 */
export default function IndexAlertsBanner() {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/me/index-alerts")
      .then((r) => setAlerts((r.data.alerts || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  if (alerts.length === 0) return null;

  return (
    <section
      data-testid="index-alerts-banner"
      className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2 shrink-0">
          <AlertTriangle size={16} className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="uppercase-label text-amber-800 inline-flex items-center gap-1">
            <TrendingDown size={12} /> Recent Health Index dip{alerts.length > 1 ? "s" : ""}
          </div>
          <p className="text-sm text-[color:var(--mano-ink)] mt-1 leading-relaxed">
            Your latest voice screenings show a dip. Nothing to worry about —
            we've already sent you (and your care team) a gentle heads-up.
          </p>
          <ul className="mt-3 space-y-1.5">
            {alerts.map((a) => (
              <li
                key={`${a.index_id}-${a.iso_week}`}
                data-testid={`alert-row-${a.index_id}-${a.iso_week}`}
                className="text-xs flex items-center justify-between gap-2 rounded-lg bg-white/80 border border-amber-100 px-3 py-2"
              >
                <span className="font-semibold text-amber-900">{a.index_code}</span>
                <span className="text-[color:var(--mano-ink-soft)]">
                  {a.previous_score} → <b className="text-red-700">{a.current_score}</b>{" "}
                  <span className="text-red-700 font-semibold">−{a.drop} pts</span>
                </span>
                <span className="text-[10px] text-[color:var(--mano-ink-soft)]">
                  {new Date(a.created_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
